import { pageEngagement } from '@wix/bi-logger-new-help-center/v2';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { UserEngagementPinger } from './UserEngagementPinger';
import { BI } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { useMaxScrollPercentage } from '../../hooks/useMaxScrollPercentage';
import { PageType } from '../../types';
import { PageData } from '../PageTemplate';

type UserEngagementProps = {
  pageType: PageType;
  pageData: PageData;
  itemType: string;
  itemId?: string;
};

export const UserEngagement: FunctionComponent<UserEngagementProps> = ({
  pageData,
  pageType,
  itemType,
}) => {
  const { sendBIEvent } = useBI();
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [maxScrollPercentage] = useMaxScrollPercentage();
  const router = useRouter();
  const { locale } = router;

  const isPageFullyVisible = (): boolean => {
    const page = document.querySelector(BI.SELECTORS.PAGE);
    const header = document.querySelector(BI.SELECTORS.HEADER);
    return page
      ? page.scrollHeight < window.innerHeight - (header?.scrollHeight || 0)
      : false;
  };

  const onPing = async (activities: string[]): Promise<void> => {
    if (pageType !== PageType.Article) {
      return;
    }

    await sendBIEvent(
      pageEngagement({
        source_name: BI.SOURCE_NAMES.ARTICLE,
        kb_lang: locale as string,
        item_id: pageData.article?.id,
        item_type: itemType,
        activity_type: activities.join(','),
        max_scroll: isPageFullyVisible() ? 100 : maxScrollPercentage,
        max_scroll_at_first_position: isPageFullyVisible(),
      })
    );
  };

  useEffect(() => {
    const handleRouteChange = () => {
      setIsLoadingPage(true);
    };

    const handleRouteChangeComplete = () => {
      setIsLoadingPage(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  if (isLoadingPage) {
    return null;
  }

  return (
    <UserEngagementPinger
      key={pageType}
      onPing={onPing}
      pingInterval={BI.USER_ENGAGEMENT_PINGER_INTERVAL}
    />
  );
};
